// apps/platform/src/lib/benefitTypesClient.ts
//
// Thin wrapper around the `write-benefit-type` edge function (Platform Admin
// CRUD over benefit_types + benefit_type_extra_fields). Reads use supabase
// directly via RLS — Platform Admins are exempted from tenant scoping.

import { supabase } from "./supabase";

export type BaseBehavior = "space" | "partner" | "service" | "promo";
export type ExtraFieldInputType =
  | "text" | "textarea" | "number" | "boolean"
  | "date" | "datetime" | "dropdown" | "multi_select";

export interface BenefitType {
  id: string;
  tenant_id: string;
  slug: string;
  label: string;
  base_behavior: BaseBehavior;
  icon: string | null;
  color: string | null;
  sort_order: number;
  enabled: boolean;
  deprecated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BenefitTypeExtraField {
  id: string;
  benefit_type_id: string;
  key: string;
  admin_label: string;
  mobile_label: string;
  input_type: ExtraFieldInputType;
  required: boolean;
  visible_to_member: boolean;
  sort_order: number;
  config: Record<string, unknown>;
  deprecated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TypeCreateInput {
  slug: string;
  label: string;
  base_behavior: BaseBehavior;
  icon?: string | null;
  color?: string | null;
  enabled?: boolean;
}

export type TypeUpdateInput = Partial<Omit<TypeCreateInput, "base_behavior">>;

export interface FieldCreateInput {
  benefit_type_id: string;
  key: string;
  admin_label: string;
  mobile_label: string;
  input_type: ExtraFieldInputType;
  required?: boolean;
  visible_to_member?: boolean;
  sort_order?: number;
  config?: Record<string, unknown>;
}

export type FieldUpdateInput = Partial<Omit<FieldCreateInput, "benefit_type_id">>;

interface EdgeResponse<T> {
  ok?: boolean;
  row?: T;
  count?: number;
  error?: string;
}

async function invoke<T>(body: Record<string, unknown>): Promise<EdgeResponse<T>> {
  const { data, error } = await supabase.functions.invoke("write-benefit-type", { body });
  if (error) {
    return { error: error.message || "Falha de rede." };
  }
  return (data ?? {}) as EdgeResponse<T>;
}

// ─── Reads (RLS-gated SELECT) ───────────────────────────────────────────────

export async function listBenefitTypes(tenantId: string) {
  const { data, error } = await supabase
    .from("benefit_types")
    .select("*")
    .eq("tenant_id", tenantId)
    // KLUB-264 — same order as the app's chips (deriveTypesFromFeed in
    // apps/mobile/lib/typeChips.ts): sort_order, ties broken by slug. Legacy
    // rows can share a sort_order, and any other tiebreak would show the
    // table in an order the app does not use.
    .order("sort_order", { ascending: true })
    .order("slug", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as BenefitType[];
}

export async function listExtraFields(benefitTypeId: string) {
  const { data, error } = await supabase
    .from("benefit_type_extra_fields")
    .select("*")
    .eq("benefit_type_id", benefitTypeId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as BenefitTypeExtraField[];
}

// ─── Writes (edge fn) ───────────────────────────────────────────────────────

export function createBenefitType(tenantId: string, data: TypeCreateInput) {
  return invoke<BenefitType>({ op: "type.create", tenant_id: tenantId, data });
}

export function updateBenefitType(id: string, data: TypeUpdateInput) {
  return invoke<BenefitType>({ op: "type.update", id, data });
}

export function deprecateBenefitType(id: string) {
  return invoke<BenefitType>({ op: "type.deprecate", id });
}

// KLUB-264 — sort_order is not an input of create or update: the server
// appends a new type last, and only this reorder numbers types. It sends the
// tenant's FULL list of types (deprecated and disabled included) in the new
// order plus where "Todos" sits on that scale (null = first). One transaction
// renumbers the types 1..N and stores the position, or neither happens.
export function reorderBenefitTypes(tenantId: string, ids: string[], allPosition: number | null) {
  return invoke<never>({ op: "type.reorder", tenantId, ids, allPosition });
}

export function createExtraField(tenantId: string, data: FieldCreateInput) {
  return invoke<BenefitTypeExtraField>({ op: "field.create", tenant_id: tenantId, data });
}

export function updateExtraField(id: string, data: FieldUpdateInput) {
  return invoke<BenefitTypeExtraField>({ op: "field.update", id, data });
}

export function deprecateExtraField(id: string) {
  return invoke<BenefitTypeExtraField>({ op: "field.deprecate", id });
}

export function reorderExtraFields(order: Array<{ id: string; sort_order: number }>) {
  return invoke<never>({ op: "field.reorder", order });
}
